'use client';

import { CaretDown, Check, Plus, Users } from '@phosphor-icons/react/ssr';
import {
  type ElementType,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useCallback,
  useRef,
  useState,
} from 'react';

import { tv } from '../../lib/tv';
import { Command, CommandItem, CommandList } from '../ui/command';
import { Input } from '../ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '../ui/popover';
import { useCmdkActiveDescendant } from './cmdk-active-descendant';
import { useFieldControl, useFieldLabelId } from './field';
import { menuStyles } from './menu-styles';
import { textInputStyles } from './text-input';

export type ComboboxItem = {
  /** What `onValueChange` receives. Unique. */
  value: string;
  /** The text shown, searched and highlighted. */
  label: string;
  /** Figma "Avatar": a 24px Avatar (or icon) before the label, e.g. `<Avatar size={24} name="…" alt="" />`. */
  avatar?: ReactNode;
  /** Figma "Meta": trailing Caption text, e.g. a role. */
  meta?: ReactNode;
  /** Extra words that match the query (e.g. an email). */
  keywords?: string[];
  disabled?: boolean;
};

export type ComboboxProps = {
  options: ComboboxItem[];
  /** The chosen value (controlled). */
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string) => void;
  /** Figma "Placeholder", e.g. "Assign an owner". */
  placeholder?: string;
  /** The typed query (controlled) - e.g. to load options from a server. */
  inputValue?: string;
  defaultInputValue?: string;
  onInputValueChange?: (query: string) => void;
  /** Does an option match the query? Defaults to a case-insensitive "contains" on label and keywords. */
  filter?: (option: ComboboxItem, query: string) => boolean;
  /**
   * Combobox List Type=Create: offer to add the typed value when no option
   * matches it exactly. Called with the query.
   */
  onCreate?: (query: string) => void;
  /** Create row label. Default: Create “query”. */
  createLabel?: (query: string) => ReactNode;
  /** Combobox List Type=Empty: the icon (default Users), title and next step (e.g. a Link). */
  emptyIcon?: ElementType<{ className?: string }>;
  emptyTitle?: (query: string) => ReactNode;
  emptyAction?: ReactNode;
  disabled?: boolean;
  id?: string;
  /** Needed when not inside a Field. */
  'aria-label'?: string;
  /** Error state without a Field (inside a Field, `error` sets it). */
  'aria-invalid'?: boolean;
  /** Class for the outer box (width, margins). */
  className?: string;
  /** The list's open state (uncontrolled by default). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
};

const CREATE = '\u0000create';

const contains = (option: ComboboxItem, query: string) => {
  const q = query.trim().toLowerCase();
  return [option.label, ...(option.keywords ?? [])].some((t) =>
    t.toLowerCase().includes(q),
  );
};

/**
 * Figma Combobox (91:11072): the Text Input box at Medium (12 left, 8 right,
 * gap 8, Body/Medium) with an 18px caret (icon/secondary; icon/tertiary when
 * disabled) that flips up while open, and the chosen option's 24px avatar
 * when filled. Typing = focus: border/focus 1.5 + Focus/Input.
 */
export const comboboxStyles = tv({
  slots: {
    root: 'w-full',
    box: 'cursor-text pr-2 pl-3',
    input: 'caret-border-focus',
    avatar: 'flex shrink-0',
    caret: [
      'text-icon-secondary size-[18px] shrink-0 transition-transform duration-[120ms] ease-out',
      'group-data-[state=open]/combobox:rotate-180 group-has-[input:disabled]/combobox:text-icon-tertiary',
    ],
    // Combobox List: the Menu surface (bg/surface, border/subtle, radius/lg,
    // 8px padding, Shadow/MD) at trigger width, 4px below, max 320 then scroll.
    panel: 'max-h-80',
    label: 'min-w-0 flex-1 truncate',
    match: 'font-semibold',
    // text/tertiary on the Active row's bg/muted is 4.49:1 - one step darker there (WCAG AA).
    meta: 'text-caption text-text-tertiary shrink-0 group-data-[selected=true]/option:text-text-secondary',
    check: 'text-icon-brand size-4 shrink-0',
    createIcon:
      'bg-brand-subtle text-icon-brand flex size-6 shrink-0 items-center justify-center rounded-full',
    createLabel: 'text-body-md-strong text-text-brand min-w-0 flex-1 truncate',
    empty: 'flex flex-col items-center gap-1.5 py-4 text-center',
    emptyIcon: 'text-icon-tertiary size-5',
    emptyTitle: 'text-body-md-strong text-text-primary',
  },
});

/** The label with the first case-insensitive match of the query in SemiBold (Figma Match + Rest). */
export function HighlightMatch({ label, query }: { label: string; query: string }) {
  const q = query.trim();
  const at = q ? label.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return <>{label}</>;
  return (
    <>
      {label.slice(0, at)}
      <span data-slot="combobox-match" className={comboboxStyles().match()}>
        {label.slice(at, at + q.length)}
      </span>
      {label.slice(at + q.length)}
    </>
  );
}

/** Figma Combobox Option: avatar, label (match in SemiBold), meta, and a check when chosen. */
export function ComboboxOption({
  item,
  query,
  checked,
  onSelect,
}: {
  item: ComboboxItem;
  query: string;
  checked: boolean;
  onSelect?: (value: string) => void;
}) {
  const styles = comboboxStyles();
  return (
    <CommandItem
      value={item.value}
      disabled={item.disabled}
      onSelect={onSelect}
      data-slot="combobox-option"
      data-checked={checked || undefined}
      className="group/option"
    >
      {item.avatar}
      <span className={styles.label()}>
        <HighlightMatch label={item.label} query={query} />
      </span>
      {item.meta && <span className={styles.meta()}>{item.meta}</span>}
      {checked && <Check aria-hidden className={styles.check()} />}
    </CommandItem>
  );
}

export type ComboboxResultsProps = {
  options: ComboboxItem[];
  query: string;
  /** The chosen value - its option shows the check. */
  checked?: string | null;
  onSelect?: (value: string) => void;
  /** Show the Create row for `query`. */
  create?: boolean;
  onCreate?: () => void;
  createLabel?: (query: string) => ReactNode;
  emptyIcon?: ElementType<{ className?: string }>;
  emptyTitle?: (query: string) => ReactNode;
  emptyAction?: ReactNode;
  /** Names the listbox (a Field label id) … */
  'aria-labelledby'?: string;
  /** … or labels it. */
  label?: string;
  /** Receives the listbox element. */
  listRef?: Ref<HTMLDivElement>;
};

/**
 * Figma Combobox List content: Results (options), Create (options, a divider
 * and the Create row) or Empty (icon, title, next step). The listbox holds
 * only options (the divider is decorative); the Empty message sits after it
 * as a status. Renders inside a cmdk Command.
 */
export function ComboboxResults({
  options,
  query,
  checked,
  onSelect,
  create = false,
  onCreate,
  createLabel = (q) => `Create “${q}”`,
  emptyIcon: EmptyIcon = Users,
  emptyTitle = (q) => `No results for “${q}”`,
  emptyAction,
  'aria-labelledby': labelledBy,
  label,
  listRef,
}: ComboboxResultsProps) {
  const styles = comboboxStyles();
  const empty = !options.length && !create;
  return (
    <>
      <CommandList
        ref={listRef}
        label={label}
        aria-labelledby={labelledBy}
        data-slot="combobox-list"
      >
        {options.map((item) => (
          <ComboboxOption
            key={item.value}
            item={item}
            query={query}
            checked={item.value === checked}
            onSelect={onSelect}
          />
        ))}
        {create && (
          <>
            {options.length > 0 && (
              // Figma Divider (Subtle): the Menu divider, decorative - a listbox holds only options.
              <div
                aria-hidden
                data-slot="command-separator"
                className={menuStyles().divider()}
              />
            )}
            <CommandItem value={CREATE} onSelect={onCreate} data-slot="combobox-create">
              <span aria-hidden className={styles.createIcon()}>
                <Plus className="size-3.5" />
              </span>
              <span className={styles.createLabel()}>{createLabel(query.trim())}</span>
            </CommandItem>
          </>
        )}
      </CommandList>
      {empty && (
        <div data-slot="combobox-empty" role="status" className={styles.empty()}>
          <EmptyIcon aria-hidden className={styles.emptyIcon()} />
          <span className={styles.emptyTitle()}>{emptyTitle(query)}</span>
          {emptyAction}
        </div>
      )}
    </>
  );
}

/**
 * The combobox input. Inside the cmdk root so it can follow the active
 * option for `aria-activedescendant`.
 */
function ComboboxInput({
  listId,
  open,
  ...props
}: Parameters<typeof Input>[0] & {
  /** The listbox id once it has mounted (it renders in a portal, a pass later). */
  listId: string | undefined;
  open: boolean;
}) {
  // The listbox and its active option live in a portal that mounts a pass
  // later, and cmdk doesn't expose item ids - so follow them in the DOM.
  const ids = useCmdkActiveDescendant(listId, open);
  return (
    <Input
      role="combobox"
      aria-expanded={open}
      aria-controls={ids.list}
      aria-autocomplete="list"
      aria-activedescendant={ids.active}
      autoComplete="off"
      {...props}
    />
  );
}

/**
 * Editable select: type to filter a long list (people, templates, tags),
 * arrow keys to move, Enter to pick, Escape to close (Figma: Components ↳
 * Search & Combobox ↳ Combobox). shadcn's combobox pattern - cmdk (Command)
 * for the list and keyboard, a Radix Popover anchored to the field - with a
 * real `role="combobox"` input: `aria-expanded`, `aria-controls` the listbox
 * and `aria-activedescendant` the active option. Inside a Field it picks up
 * the label, description, invalid and disabled, and the listbox is named by
 * the Field label. Use Select for ≤ 7 fixed options.
 */
export function Combobox({
  options,
  value: valueProp,
  defaultValue = null,
  onValueChange,
  placeholder,
  inputValue: queryProp,
  defaultInputValue = '',
  onInputValueChange,
  filter = contains,
  onCreate,
  createLabel,
  emptyIcon,
  emptyTitle,
  emptyAction,
  disabled: disabledProp,
  id,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  className,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
}: ComboboxProps) {
  const [valueState, setValueState] = useState(defaultValue);
  const value = valueProp === undefined ? valueState : valueProp;
  const [queryState, setQueryState] = useState(defaultInputValue);
  const query = queryProp ?? queryState;
  // Typing: the input shows the query; otherwise the chosen option's label.
  const [editing, setEditing] = useState(Boolean(defaultInputValue));
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const [active, setActive] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [listId, setListId] = useState<string>();
  const attachList = useCallback((el: HTMLDivElement | null) => {
    // cmdk re-attaches its composed ref on every render (null, then the
    // element), so keep the last real id; `open` says whether it is shown.
    if (el) setListId(el.id);
  }, []);
  const labelId = useFieldLabelId();
  const { disabled, ...control } = useFieldControl({
    id,
    disabled: disabledProp,
    'aria-invalid': ariaInvalid || undefined,
  });
  const styles = comboboxStyles();
  const box = textInputStyles({ size: 'md' });

  const selected = options.find((o) => o.value === value);
  const search = editing ? query : '';
  const shown = search ? options.filter((o) => filter(o, search)) : options;
  const canCreate = (q: string) =>
    Boolean(onCreate && q.trim()) &&
    !options.some((o) => o.label.toLowerCase() === q.trim().toLowerCase());
  const create = canCreate(search);

  const firstOf = (list: ComboboxItem[], withCreate: boolean) =>
    list.find((o) => !o.disabled)?.value ?? (withCreate ? CREATE : '');

  const setOpen = (next: boolean) => {
    if (next === open) return;
    if (next) setActive(selected && !editing ? selected.value : firstOf(shown, create));
    setOpenState(next);
    onOpenChange?.(next);
  };
  const setQuery = (next: string) => {
    setQueryState(next);
    onInputValueChange?.(next);
  };
  const close = () => {
    setOpen(false);
    setEditing(false);
    setQuery('');
  };
  const choose = (next: string) => {
    setValueState(next);
    onValueChange?.(next);
    close();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
      }
      // Closed: keep cmdk's list keys (and its Enter preventDefault) away.
      e.stopPropagation();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      close();
    }
    // Home / End move the caret in the text, not the active option.
    if (e.key === 'Home' || e.key === 'End') e.stopPropagation();
  };

  return (
    <Command
      shouldFilter={false}
      vimBindings={false}
      value={active}
      onValueChange={setActive}
      label={ariaLabel}
      data-slot="combobox"
      className={styles.root({ className })}
    >
      <Popover open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
        <PopoverAnchor asChild>
          {/* Clicking the box (caret, avatar, padding) opens the list and keeps focus in the input. */}
          <div
            ref={boxRef}
            data-slot="combobox-box"
            data-state={open ? 'open' : 'closed'}
            className={box.root({ className: [styles.box(), 'group/combobox'] })}
            onMouseDown={(e) => {
              if (disabled || e.target === inputRef.current) return;
              e.preventDefault();
              inputRef.current?.focus();
              setOpen(!open);
            }}
          >
            {selected?.avatar && !editing && (
              <span className={styles.avatar()}>{selected.avatar}</span>
            )}
            <ComboboxInput
              ref={inputRef}
              listId={listId}
              open={open}
              {...control}
              disabled={disabled}
              aria-label={ariaLabel}
              placeholder={placeholder}
              value={editing ? query : (selected?.label ?? '')}
              onChange={(e) => {
                const next = e.target.value;
                setQuery(next);
                setEditing(true);
                const nextShown = next ? options.filter((o) => filter(o, next)) : options;
                setActive(firstOf(nextShown, canCreate(next)));
                if (!open) {
                  setOpenState(true);
                  onOpenChange?.(true);
                }
              }}
              onClick={() => setOpen(true)}
              onBlur={() => open && close()}
              onKeyDown={handleKeyDown}
              className={styles.input()}
            />
            <CaretDown aria-hidden className={styles.caret()} />
          </div>
        </PopoverAnchor>
        <PopoverContent
          // A positioned listbox, not a dialog: focus stays in the input.
          role={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => {
            if (boxRef.current?.contains(e.target as Node)) e.preventDefault();
          }}
          // Keep focus in the input while an option is clicked.
          onMouseDown={(e) => e.preventDefault()}
          className={`w-(--radix-popover-trigger-width) ${styles.panel()}`}
        >
          <ComboboxResults
            listRef={attachList}
            label={ariaLabel}
            aria-labelledby={labelId}
            options={shown}
            query={search}
            checked={value}
            onSelect={choose}
            create={create}
            onCreate={() => {
              onCreate?.(search.trim());
              close();
            }}
            createLabel={createLabel}
            emptyIcon={emptyIcon}
            emptyTitle={emptyTitle}
            emptyAction={emptyAction}
          />
        </PopoverContent>
      </Popover>
    </Command>
  );
}
